import React, { useState, memo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import {
  FormControl,
  FormLabel,
  Checkbox,
  FormControlLabel,
  TextField,
  Box,
  Switch,
  IconButton,
  RadioGroup,
  Radio,
} from "@mui/material";
import { ALL_REGIONS, PLC_ADMIN, PRODUCT_OPTIONS, REGION_OPTIONS, REGION_WW, ROLE_REGION_MAP_BULK_UPLOAD } from "constants/ocplcConstants";
import { Delete } from "@mui/icons-material";
import { bulkUpload, downloadTemplate } from "actions/ocplcAction";
import { useDispatch } from "react-redux";

const CheckboxGroupWithLabel = ({ label, value, onChange, options, disableRegions }) => {
  return (
    <FormControl component="fieldset" sx={{ mb: 2 }}>
      <FormLabel component="legend"  sx={{ fontWeight: "bold", fontSize:"1rem", color: "#000 !important", }}>
        {label}
      </FormLabel>
      <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={value.includes(option.value)}
                onChange={(e) => onChange(e.target.checked, option.value)}
                disabled={disableRegions.includes(option.value)}
              />
            }
            label={option.label}
          />
        ))}
      </Box>
    </FormControl>
  );
};

const RadioGroupWithLabel = ({ label, value, onChange, options }) => {
  return (
    <FormControl component="fieldset" sx={{ mb: 2 }}>
      <FormLabel component="legend" sx={{ fontWeight: "bold", fontSize: "1rem", color: "#000 !important" }}>
        {label}
      </FormLabel>
      <RadioGroup value={value} onChange={onChange} row >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={option.label}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

function BulkUploadDialog({
  open, onClose, title, userEmail, BackdropProps, PaperProps, userRole
}) {
  const [file, setFile] = useState(null);
  const [region, setRegion] = useState([]);
  const [productGroup, setProductGroup] = useState("");
  const [goToMarket, setGoToMarket] = useState(false);
  const fileInputRef = useRef();
  const dispatch = useDispatch();

  const handleDownload = () => {
    dispatch(downloadTemplate(region, productGroup, goToMarket, onClose));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setFile(e.dataTransfer.files[0] || null);
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleUpload = () => {
    if (!file) return;

    dispatch(
      bulkUpload(file, userEmail, () => {
        setFile(null);
        onClose?.();
      })
    );
  };

  const handleRegion = (checked, value) => {
    setRegion((prev) => {
      const updated = checked ? [...prev, value] : prev.filter((item) => item !== value);
      const hasValidRegion = updated.length > 0 && !updated.includes(REGION_WW);
      if (!hasValidRegion) {
        setGoToMarket(false);
      }
      return updated;
    });
  };
  

  const handleProductGroup = (e) => {
      setProductGroup(e.target.value);
  };


  const handleRemoveFile = () => {
   setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isFormValid = region.length > 0 && productGroup !== "";
  const disableRegions = ALL_REGIONS.filter((r) =>{
    const isProductGroupNotSelected = productGroup === "";
    const roleRestricted = !ROLE_REGION_MAP_BULK_UPLOAD[userRole]?.region?.includes(r)
    const wwLogic = (region.includes(REGION_WW) && r !== REGION_WW) || (region.some((item) => item !== REGION_WW) && r === REGION_WW);
    const isAdminSingleRegionSelected = userRole === PLC_ADMIN && region.length > 0 && !region.includes(r);
    return isProductGroupNotSelected || roleRestricted || wwLogic || isAdminSingleRegionSelected;
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
          width: 680,
          p: 1,
          m: 0,
          ...PaperProps?.sx,
        },
      }}
      slotProps={{ backdrop: BackdropProps }}
    >
      <DialogTitle>{title}</DialogTitle>

      <DialogContent 
        dividers 
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <RadioGroupWithLabel
          label="Select Product Group:"
          value={productGroup}
          onChange={handleProductGroup}
          options={PRODUCT_OPTIONS}
        />
        <CheckboxGroupWithLabel
          label="Select Region:"
          value={region}
          onChange={handleRegion}
          options={REGION_OPTIONS}
          disableRegions={disableRegions}
        />

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '18px', mr: 1 }}>Go to Market selection</Typography>
          <Switch
            checked={goToMarket}
            onChange={(e) => setGoToMarket(e.target.checked)}
            disabled={productGroup === ""|| region.length === 0 || region.includes('WW')}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            disabled={!isFormValid}
            onClick={handleDownload}
          >
            Download Template
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
          <TextField
            inputProps={{ readOnly: true }}
            fullWidth
            value={file ? file.name : ''}
            onClick={() => fileInputRef.current.click()}
          />
          <input
            type="file"
            accept=".xls,.xlsx,.csv"
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!file}
          >
            Upload
          </Button>
        {file && (
            <IconButton onClick={handleRemoveFile} aria-label="Remove file">
              <Delete />
            </IconButton>
          )}
        </Box>

      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", ml: 1, mb: 1}}>
        <Button
          variant="outlined"
          onClick={onClose}
          color="primary"
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BulkUploadDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  BackdropProps: PropTypes.object,
  PaperProps: PropTypes.object,
};

export default memo(BulkUploadDialog);